import { useState } from 'react';
import {
  Modal,
  Stack,
  Button,
  ScrollArea,
  Alert,
  Group,
  Text,
  Card,
  Badge,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconChefHat,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../../hooks/useAuth';
import { MealPlan } from '../../types/mealPlan';
import { formatRecipeSource, formatDateRange, formatCreatedDate } from '../../utils/mealPlanUtils';

interface FailedMealPlansModalProps {
  opened: boolean;
  onClose: () => void;
  failedMealPlans: MealPlan[];
  onMealPlansUpdated: () => void;
}

export const FailedMealPlansModal = ({
  opened,
  onClose,
  failedMealPlans,
  onMealPlansUpdated,
}: FailedMealPlansModalProps) => {
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);

  const clearAllFailedMealPlans = async () => {
    if (!user || isProcessing) return;

    setIsProcessing(true);

    try {
      const token = await user.getIdToken();
      console.log('Calling clear all failed meal plans API...');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/meals/plans/status/failed`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('API response status:', response.status);
      console.log('API response statusText:', response.statusText);

      if (!response.ok) {
        const data = await response.json();
        console.error('API error response:', data);
        throw new Error(data.error || 'Failed to clear all meal plans');
      }

      const result = await response.json();
      console.log('API success response:', result);

      notifications.show({
        title: 'Meal plans cleared',
        message: 'All failed meal plans have been removed',
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 6000,
      });

      onMealPlansUpdated();
      onClose();
    } catch (error: any) {
      console.error('Error clearing all failed meal plans:', error);
      notifications.show({
        title: 'Clear failed',
        message: error.message || 'Failed to clear meal plans. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 10000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600} size="lg">Meal plans failed</Text>}
      size="md"
      centered
      radius="lg"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="lg">
        {failedMealPlans.length === 0 ? (
          <Alert icon={<IconAlertCircle size={16} color="green" />} color="green" radius="lg">
            <Text size="sm">No failed meal plans to show. All meal plan creations completed successfully!</Text>
          </Alert>
        ) : (
          <>
            {/* Helpful message for users */}
            <Text size="sm" c="dimmed">
              These meal plans couldn't be created. Check the error message below and adjust planning dates and preferences accordingly.
            </Text>
            
            {/* Show failed meal plans in cards */}
            <Stack gap="md">
              {failedMealPlans.map((plan) => (
                <Card key={plan.id} p="md" withBorder radius="lg" style={{ borderColor: 'var(--mantine-color-red-3)' }}>
                  <Stack gap="xs">
                    {/* Header with icon */}
                    <Group gap="sm">
                      <IconChefHat size={18} color="var(--mantine-color-red-6)" />
                      <Text fw={500} size="sm">
                        {formatDateRange(plan)}
                      </Text>
                      <Badge color="red" variant="light" size="sm">
                        Failed
                      </Badge>
                    </Group>
                    
                    {/* Preferences */}
                    <Text size="xs" c="dimmed">
                      {`Created ${formatCreatedDate(plan.createdAt)} • ${formatRecipeSource(plan.preferences.recipeSources)}`}
                    </Text>
                    
                    {/* Error message */}
                    {plan.generationStatus.errorMessage && (
                      <Text size="xs" c="red">
                        {plan.generationStatus.errorMessage}
                      </Text>
                    )}
                  </Stack>
                </Card>
              ))}
            </Stack>

            {/* Clear all button */}
            <Group justify="flex-end" gap="lg">
              <UnstyledButton onClick={onClose}>
                <Text size="sm" c="dimmed">Cancel</Text>
              </UnstyledButton>
              <Button
                variant="light"
                color="red"
                onClick={clearAllFailedMealPlans}
                loading={isProcessing}
                disabled={isProcessing}
              >
                Clear all
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};
